<template>
  <div class="suf">
    <h3 class="suf-title">{{ entityId ? 'Edit user' : 'Add a new user' }}</h3>

    <div v-if="busy" class="suf-busy">Working…</div>

    <div class="suf-grid">
      <!-- Companies (multi) -->
      <div class="suf-field suf-col-2">
        <label>Companies <span class="suf-hint">(Ctrl/⌘-click to select more than one)</span></label>
        <select multiple size="4" class="suf-input suf-multi" v-model="companies">
          <option v-for="c in companyList" :key="'c-' + c.value" :value="c.value">{{ c.text }}</option>
        </select>
      </div>

      <!-- User Type / roles (multi) -->
      <div class="suf-field suf-col-2">
        <label>User Type <span class="suf-hint">— controls permissions (e.g. Customer vs Admin)</span></label>
        <select multiple size="4" class="suf-input suf-multi" v-model="roles">
          <option v-for="t in userTypes" :key="'t-' + t.value" :value="t.value">{{ t.text }}</option>
        </select>
      </div>

      <!-- Sales territory scope — shown only for the 'Sales' role. Reuses Account Setup → Sales Territories.
           A 'Sales' user only sees the Customer Insights data for the customers handled by the reps in these
           territories (Master / Sales All see everything). -->
      <div class="suf-field suf-col-2" v-if="isSalesRole">
        <label>Sales Territories <span class="suf-hint">— a 'Sales' user only sees report data for these territories</span></label>
        <select multiple size="5" class="suf-input suf-multi" v-model="userTerritories">
          <option v-for="t in salesTerritoriesList" :key="'terr-' + t" :value="t">{{ t }}</option>
        </select>
        <div v-if="!salesTerritoriesList.length" class="suf-hint">No territories defined yet — add them in Account Setup → Sales Territories.</div>
      </div>

      <div class="suf-field">
        <label>First Name</label>
        <input type="text" class="suf-input" v-model="firstName" />
      </div>
      <div class="suf-field">
        <label>Last Name</label>
        <input type="text" class="suf-input" v-model="lastName" />
      </div>

      <div class="suf-field suf-col-2">
        <label>Title</label>
        <input type="text" class="suf-input" v-model="title" placeholder="title" />
      </div>

      <div class="suf-field">
        <label>Email (will be the username) <span class="suf-req">*</span></label>
        <input type="email" class="suf-input" :class="{ 'is-missing': !email }" v-model="email" required
               placeholder="name@company.com — required" @input="onEmailInput" />
      </div>
      <div class="suf-field">
        <label>Phone Number</label>
        <input type="tel" class="suf-input" v-model="phone" v-mask="'(###) ###-####'" placeholder="(000) 000-0000" />
      </div>

      <!-- Live lookup of accounts already using this email (Delete to free it up). -->
      <div v-if="emailSearchBusy || emailMatches.length" class="suf-field suf-col-2 suf-matches">
        <div class="suf-matches-head">
          <template v-if="emailSearchBusy">Checking the database…</template>
          <template v-else>⚠ Already in use — delete the account to free the email, or link/edit it instead:</template>
        </div>
        <div v-for="m in emailMatches" :key="'em-' + m.id" class="suf-match">
          <span class="suf-match-info">
            <strong>{{ m.email }}</strong>
            <span class="suf-match-sub">{{ m.name || '(no name)' }}<template v-if="m.customerId"> · {{ m.customerId }}</template><template v-if="!m.active"> · inactive</template></span>
          </span>
          <button type="button" class="suf-match-del" :disabled="emailDeleteBusy === m.id" @click="deleteMatch(m)">
            {{ emailDeleteBusy === m.id ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>

      <div class="suf-field suf-col-2">
        <label>Address Name</label>
        <input type="text" class="suf-input" v-model="address.name" />
      </div>

      <div class="suf-field">
        <label>Address</label>
        <input type="text" class="suf-input" v-model="address.street" />
      </div>
      <div class="suf-field">
        <label>Address Line 2</label>
        <input type="text" class="suf-input" v-model="address.street2" />
      </div>
      <div class="suf-field suf-col-2">
        <label>Address Line 3</label>
        <input type="text" class="suf-input" v-model="address.street3" />
      </div>

      <div class="suf-field">
        <label>City</label>
        <input type="text" class="suf-input" v-model="address.city" />
      </div>
      <div class="suf-field">
        <label>State</label>
        <select class="suf-input" v-model="address.state">
          <option value="">Select State</option>
          <option v-for="s in states" :key="'s-' + s.code" :value="s.code">{{ s.name }}</option>
        </select>
      </div>
      <div class="suf-field">
        <label>Postal Code</label>
        <input type="text" class="suf-input" v-model="address.postalCode" />
      </div>
      <div class="suf-field">
        <label>Country</label>
        <select class="suf-input" v-model="address.country" @change="loadStates(address.country)">
          <option value="">Select Country</option>
          <option v-for="c in countries" :key="'co-' + c.code" :value="c.code">{{ c.name }}</option>
        </select>
      </div>

      <div class="suf-field">
        <label>Status</label>
        <label class="suf-check"><input type="checkbox" v-model="active" /> Active <span class="suf-hint">(unchecked = inactive)</span></label>
      </div>
      <div class="suf-field">
        <label>Approval</label>
        <label class="suf-check"><input type="checkbox" v-model="pending" /> Approved <span class="suf-hint">(unchecked = pending)</span></label>
      </div>
    </div>

    <div v-if="errorMsg" class="suf-error">{{ errorMsg }}</div>

    <div class="suf-actions">
      <button type="button" class="suf-save" :disabled="busy" @click="save">
        {{ busy ? 'Saving…' : (entityId ? 'Save User' : 'Add User') }}
      </button>
      <button type="button" class="suf-cancel" :disabled="busy" @click="$emit('cancel')">Cancel</button>
    </div>
  </div>
</template>

<script>
import APIService from "@/assets/js/APIService";
import FeApiService from "@/assets/js/FeApiService";
import httpClient from "@/assets/js/HttpClient";
const apiService = new APIService();
const feApi = new FeApiService();

export default {
  name: "SetupUserForm",
  props: {
    // null/empty => add a new user; otherwise the user id (GUID) to edit.
    entityId: { type: [String, Number], default: null },
    // For a NEW user, preselect this company id.
    presetCompanyId: { type: [String, Number], default: null },
  },
  data() {
    return {
      companies: [],
      companyList: [],
      roles: [],
      userTypes: [],
      id: "",
      customerId: "",
      firstName: "",
      lastName: "",
      title: "",
      email: "",
      phone: "",
      dateRegistered: "",
      active: true,
      pending: false,
      address: { name: "", street: "", street2: "", street3: "", city: "", state: "", postalCode: "", country: "US" },
      states: [],
      countries: [],
      busy: false,
      errorMsg: "",
      // Live "email already in use" lookup.
      emailMatches: [],
      emailSearchBusy: false,
      emailDeleteBusy: "",
      _emailTimer: null,
      // Sales territory scope (role === 'Sales'). Options come from Account Setup → Sales Territories.
      salesTerritoriesList: [],
      userTerritories: [],
    };
  },
  computed: {
    // The territory selector + its save only apply to the 'Sales' role.
    isSalesRole() { return (this.roles || []).indexOf("Sales") >= 0; },
  },
  mounted() {
    this.loadCompanies();
    this.loadUserTypes();
    this.loadCountries();
    this.loadSalesTerritoriesList();
    if (this.entityId) {
      this.loadUser();
      this.loadUserTerritories();
    } else {
      const pid = Number(this.presetCompanyId) || 0;
      if (pid) this.companies = [pid];
      this.loadStates("US");
    }
  },
  methods: {
    loadCompanies() {
      apiService.getCompaniesList().then((d) => { this.companyList = d || []; }).catch(() => {});
    },
    loadUserTypes() {
      apiService.getUserTypes().then((d) => { this.userTypes = d || []; }).catch(() => {});
    },
    loadCountries() {
      apiService.getCountries().then((d) => { this.countries = d || []; }).catch(() => {});
    },
    loadStates(country) {
      const c = country || "US";
      apiService.getStatesByCountry(c).then((d) => { this.states = d || []; }).catch(() => { this.states = []; });
    },
    // Distinct territory names from Account Setup → Sales Territories (rpt_SalesTerritory) — the scope options.
    loadSalesTerritoriesList() {
      httpClient.get("/report/sales-territory")
        .then((res) => {
          const rows = (res && res.data) || [];
          const seen = {};
          const out = [];
          rows.forEach((r) => {
            const t = (r && r.territory) ? String(r.territory).trim() : "";
            if (t && !seen[t.toLowerCase()]) { seen[t.toLowerCase()] = true; out.push(t); }
          });
          out.sort();
          this.salesTerritoriesList = out;
        })
        .catch(() => { this.salesTerritoriesList = []; });
    },
    // Territories already assigned to THIS user (edit mode).
    loadUserTerritories() {
      if (!this.entityId) return;
      httpClient.get("/report/user-territory", { params: { userId: this.entityId } })
        .then((res) => { this.userTerritories = (res && res.data) || []; })
        .catch(() => { this.userTerritories = []; });
    },
    // Persist the user's territory scope (Master-only). Sends [] when the user isn't a 'Sales' user, so
    // changing a user's role away from Sales also clears their old scope. Returns a promise.
    saveUserTerritories(userId) {
      if (!userId) return Promise.resolve();
      const terrs = this.isSalesRole ? (this.userTerritories || []) : [];
      return httpClient.post("/report/user-territory", { userId: userId, territories: terrs }).catch(() => {});
    },
    loadUser() {
      this.busy = true;
      apiService
        .getUser({ id: this.entityId })
        .then((u) => {
          this.busy = false;
          this.companies = u.companies || [];
          this.id = u.id;
          this.customerId = u.customerId;
          this.firstName = u.firstName || "";
          this.lastName = u.lastName || "";
          this.title = u.title || "";
          this.email = u.email || "";
          this.phone = u.phone || "";
          this.dateRegistered = u.dateRegistered;
          this.active = !!u.active;
          this.pending = !!u.pending;
          this.roles = u.roles || [];
          this.address = Object.assign(
            { name: "", street: "", street2: "", street3: "", city: "", state: "", postalCode: "", country: "US" },
            u.address || {}
          );
          this.loadStates(this.address.country || "US");
        })
        .catch(() => {
          this.busy = false;
          this.errorMsg = "Couldn't load this user — please try again.";
        });
    },
    save() {
      if (this.busy) return;
      if (!this.email) { this.errorMsg = "An email address is required."; return; }
      this.busy = true;
      this.errorMsg = "";
      let req;
      if (this.entityId) {
        // Edit — admin PUT /user/{id}.
        req = apiService.updateUser({
          id: this.entityId,
          companies: this.companies,
          customerId: this.customerId,
          firstName: this.firstName,
          lastName: this.lastName,
          title: this.title,
          email: this.email,
          phone: this.phone,
          dateRegistered: this.dateRegistered,
          active: this.active,
          pending: this.pending,
          roles: this.roles,
          address: this.address,
          error: false,
        });
      } else {
        // Create — Master-only setup-user-create (creates the account with the chosen
        // roles/companies, emails a set-password link, and logs the outcome as an event).
        req = feApi.createSetupUser({
          companyId: Number(this.presetCompanyId) || (this.companies && this.companies[0]) || 0,
          companies: this.companies,
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          title: this.title,
          phone: this.phone,
          active: this.active,
          pending: this.pending,
          roles: this.roles,
          address: this.address,
        });
      }
      req
        .then((res) => {
          // Persist the territory scope alongside the user. Use the edited id, or the new id from the create
          // response when available (otherwise it can be assigned by re-editing the user).
          const savedId = this.entityId || this.id ||
            (res && (res.id || res.userId || (res.data && (res.data.id || res.data.userId)))) || "";
          const done = () => { this.busy = false; this.$emit("saved", { kind: "user" }); };
          if (savedId) { this.saveUserTerritories(savedId).then(done); } else { done(); }
        })
        .catch((err) => {
          this.busy = false;
          this.errorMsg = this._errText(err) || "Couldn't save the user — please check the fields and try again.";
        });
    },
    // Surface the real server error (plain string, ProblemDetails title, or a
    // validation errors{} dictionary) so the admin sees WHY it failed.
    _errText(err) {
      // The API client wraps some errors as { error: axiosError } and others as the
      // raw axios error — handle both so we always surface the real server message.
      const resp = (err && err.response) || (err && err.error && err.error.response) || null;
      const r = resp && resp.data;
      const status = resp && resp.status;
      if (!r) {
        if (status) return "Server returned " + status + (status === 404 ? " — this endpoint isn't deployed yet." : ".");
        return "";
      }
      if (typeof r === "string") return r;
      if (r.errors && typeof r.errors === "object") {
        const msgs = [];
        Object.keys(r.errors).forEach((k) => {
          const v = r.errors[k];
          if (Array.isArray(v)) v.forEach((m) => msgs.push(m));
          else if (v) msgs.push(String(v));
        });
        if (msgs.length) return msgs.join(" ");
      }
      return r.message || r.title || r.detail || "";
    },
    // As the admin types the email (the username), look up accounts already using
    // it so they can free it up (Delete) without leaving the form. Debounced.
    onEmailInput() {
      if (this.entityId) return; // only relevant when adding a NEW user
      if (this._emailTimer) { clearTimeout(this._emailTimer); this._emailTimer = null; }
      const term = (this.email || "").trim();
      if (term.length < 3) { this.emailMatches = []; this.emailSearchBusy = false; return; }
      this._emailTimer = setTimeout(() => { this.searchEmails(term); }, 300);
    },
    searchEmails(term) {
      this.emailSearchBusy = true;
      feApi
        .searchSetupUsers(term)
        .then((rows) => {
          this.emailSearchBusy = false;
          // Ignore stale responses if the box changed while we were waiting.
          if ((this.email || "").trim() !== term) return;
          this.emailMatches = Array.isArray(rows) ? rows : [];
        })
        .catch(() => {
          this.emailSearchBusy = false;
          this.emailMatches = [];
        });
    },
    deleteMatch(m) {
      if (!m || this.emailDeleteBusy) return;
      if (!window.confirm(
        "Permanently delete the account \"" + (m.email || "") + "\"?\n\n" +
        "This frees the email so it can be reused. This cannot be undone."
      )) return;
      this.emailDeleteBusy = m.id;
      feApi
        .deleteSetupUser({ userId: m.id })
        .then(() => {
          this.emailDeleteBusy = "";
          this.emailMatches = this.emailMatches.filter((x) => x.id !== m.id);
          this.errorMsg = "";
        })
        .catch((err) => {
          this.emailDeleteBusy = "";
          this.errorMsg = this._errText(err) || "Couldn't delete that account — it may still be linked to a company.";
        });
    },
  },
};
</script>

<style scoped>
.suf {
  padding: 4px 2px 8px 2px;
}
.suf-title {
  font-size: 16px;
  font-weight: 700;
  color: #2c3038;
  margin: 0 0 10px 0;
}
.suf-busy {
  font-size: 12px;
  color: #7b8794;
  margin-bottom: 8px;
}
.suf-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}
.suf-field {
  flex: 1 1 calc(50% - 7px);
  min-width: 180px;
  display: flex;
  flex-direction: column;
}
.suf-col-2 {
  flex: 1 1 100%;
}
.suf-field > label {
  font-size: 11px;
  font-weight: 600;
  color: #5b6470;
  margin-bottom: 3px;
}
.suf-hint {
  font-weight: 400;
  color: #9aa3ad;
}
.suf-input {
  border: 1px solid #d6dade;
  border-radius: 5px;
  padding: 7px 9px;
  font-size: 13px;
  color: #2c3038;
  background: #fff;
  width: 100%;
}
.suf-input:focus {
  outline: none;
  border-color: #c0392b;
}
.suf-input.is-missing {
  border-color: #e3b4ae;
  background: #fdf3f2;
}
.suf-req {
  color: #c0392b;
  font-weight: 700;
}
.suf-multi {
  padding: 2px;
}
.suf-check {
  font-size: 13px;
  color: #2c3038;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 5px;
}
.suf-error {
  margin-top: 10px;
  color: #c0392b;
  font-size: 12.5px;
  font-weight: 600;
}
.suf-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
}
.suf-save {
  background: #1e7e34;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  border-radius: 5px;
  padding: 8px 18px;
  cursor: pointer;
}
.suf-save:disabled {
  opacity: 0.6;
  cursor: default;
}
.suf-cancel {
  background: #fff;
  border: 1px solid #d6dade;
  color: #5b6470;
  font-weight: 600;
  font-size: 13px;
  border-radius: 5px;
  padding: 8px 16px;
  cursor: pointer;
}
/* Live "email already in use" lookup under the Email field. */
.suf-matches {
  background: #fdf6ec;
  border: 1px solid #f0d9a8;
  border-radius: 6px;
  padding: 8px 10px;
  gap: 6px;
}
.suf-matches-head {
  font-size: 11.5px;
  font-weight: 700;
  color: #8a6d1f;
  margin-bottom: 2px;
}
.suf-match {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
  border: 1px solid #ece0c4;
  border-radius: 5px;
  padding: 5px 8px;
}
.suf-match-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.suf-match-info strong {
  font-size: 12.5px;
  color: #2c3038;
  word-break: break-all;
}
.suf-match-sub {
  font-size: 11px;
  color: #8a93a0;
}
.suf-match-del {
  flex: 0 0 auto;
  background: #c0392b;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 11.5px;
  border-radius: 5px;
  padding: 5px 12px;
  cursor: pointer;
}
.suf-match-del:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
